/**
 * Shared mailbox-limit copy and parsing (MAILBOX_LIMIT_REACHED contract).
 */

export const MAILBOX_LIMIT_CODE = 'MAILBOX_LIMIT_REACHED' as const;

/** Query keys used by mailbox OAuth redirects; safe to strip after handling. */
export const MAILBOX_OAUTH_QUERY_KEYS = [
    'mailbox',
    'auth',
    'mailbox_code',
    'mailbox_limit',
    'mailbox_connected',
    'message',
    'mailbox_upgrade_url',
] as const;

export function safeDecodeURIComponent(value: string): string {
    try {
        return decodeURIComponent(value.replace(/\+/g, ' '));
    } catch {
        return value.replace(/\+/g, ' ');
    }
}

/** Remove mailbox OAuth callback params; preserve other query keys (e.g. focusId). */
export function stripMailboxOAuthQueryParams(search: string): string {
    if (!search || search === '?') {
        return '';
    }
    const raw = search.startsWith('?') ? search.slice(1) : search;
    const params = new URLSearchParams(raw);
    MAILBOX_OAUTH_QUERY_KEYS.forEach((k) => params.delete(k));
    const next = params.toString();
    return next ? `?${next}` : '';
}

export type MailboxLimitDetail = {
    code: typeof MAILBOX_LIMIT_CODE;
    message: string;
    limit?: number;
    connected?: number;
    upgrade_url?: string;
};

function isRecord(v: unknown): v is Record<string, unknown> {
    return typeof v === 'object' && v !== null && !Array.isArray(v);
}

export function parseMailboxLimitDetail(detail: unknown): MailboxLimitDetail | null {
    if (!isRecord(detail)) return null;
    if (detail.code !== MAILBOX_LIMIT_CODE) return null;
    const message = typeof detail.message === 'string' ? detail.message : '';
    if (!message.trim()) return null;
    return {
        code: MAILBOX_LIMIT_CODE,
        message,
        limit: typeof detail.limit === 'number' ? detail.limit : undefined,
        connected: typeof detail.connected === 'number' ? detail.connected : undefined,
        upgrade_url: typeof detail.upgrade_url === 'string' ? detail.upgrade_url : undefined,
    };
}

/** Axios/FastAPI error shape: response.data.detail may be object or string. */
export function parseMailboxLimitFromApiError(error: unknown): MailboxLimitDetail | null {
    const anyErr = error as { response?: { data?: { detail?: unknown } } };
    const detail = anyErr?.response?.data?.detail;
    return parseMailboxLimitDetail(detail);
}

/**
 * Reconnecting a disconnected/revoked row increases the CONNECTED count, so it
 * must stay behind the same plan gate as connecting a new mailbox.
 * Re-authorizing an already-CONNECTED mailbox does not.
 */
export function reconnectBlockedByMailboxLimit(
    mailbox: { status?: string | null },
    canAddMore: boolean,
): boolean {
    return (mailbox.status || '').toUpperCase() !== 'CONNECTED' && !canAddMore;
}

export function proactiveMailboxLimitMessage(limit: number, connected?: number): string {
    const usage =
        typeof connected === 'number'
            ? `You're using ${connected} of ${limit} mailboxes allowed on your current plan.`
            : `Your current plan allows ${limit} mailbox${limit === 1 ? '' : 'es'}.`;
    return `${usage} To connect another Gmail account, open Billing to manage your plan.`;
}

export function parseMailboxOAuthSearch(search: string): MailboxLimitDetail | { genericMessage: string } | null {
    const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
    const mailbox = params.get('mailbox');
    if (mailbox !== 'error') return null;

    const code = params.get('mailbox_code');
    if (code === MAILBOX_LIMIT_CODE) {
        const limitRaw = params.get('mailbox_limit');
        const connectedRaw = params.get('mailbox_connected');
        const limit = limitRaw != null && limitRaw !== '' ? Number.parseInt(limitRaw, 10) : undefined;
        const connected =
            connectedRaw != null && connectedRaw !== '' ? Number.parseInt(connectedRaw, 10) : undefined;
        const msgParam = params.get('message');
        const decoded =
            msgParam != null && msgParam.length > 0
                ? safeDecodeURIComponent(msgParam)
                : 'Mailbox limit reached. Upgrade your plan to connect more Gmail accounts.';
        const upgradeRaw = params.get('mailbox_upgrade_url');
        const upgrade_url =
            upgradeRaw != null && upgradeRaw.length > 0 ? safeDecodeURIComponent(upgradeRaw) : undefined;
        return {
            code: MAILBOX_LIMIT_CODE,
            message: decoded,
            limit: Number.isFinite(limit) ? limit : undefined,
            connected: Number.isFinite(connected) ? connected : undefined,
            ...(upgrade_url ? { upgrade_url } : {}),
        };
    }

    const msgParam = params.get('message');
    if (msgParam != null && msgParam.length > 0) {
        return { genericMessage: safeDecodeURIComponent(msgParam) };
    }
    return null;
}
